import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Container from "connectui/component/layout/Container";
import Skeleton from "connectui/component/display/Skeleton";
import ChannelRow from "../../conversations/messages/ChannelRow";
import ActionsRow from "../../conversations/messages/ActionsRow";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import ICONS from "connectui/icons/iconMap";
import SvgIcon from "connectui/component/display/SvgIcon";
import EnsoAIButton from "components/Misc/EnsoAIButton";
import ChatbotToggleButton from "components/Misc/ChatbotToggleButton";
import DisabledInput from "../../conversations/input/DisabledInput";
import ConversationInputComponent from "../../conversations/input/ConversationInputComponent";

import {MessagesContext} from "./ConversationsPanel";
import {contentTypeEnum} from "configuration/enums";
import {
  checkGuestBookingsLoaded,
  getEmailContentWrapper,
  getPredictedResponse,
} from "utilities/helperFunctions";
import {getGuestKeycardLink} from "redux/actions/accountsActions";
import {
  appendMessage,
  editMessages,
  getGuestChatOptions,
  sendMessages,
} from "redux/actions/messagesActions";
import {editGuest, updateGuest} from "redux/actions/guestsActions";
import {InboxContext} from "routes/MessagesPage";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import CreateAutoMessageMenu from "../../conversations/automessages/CreateAutoMessageMenu";
import {useFlags} from "launchdarkly-react-client-sdk";

const imageChannels = ["SMS", "airbnb"];

const useStyles = (theme) =>
  buildStyles({
    inputSkeleton: {
      width: "100%",
      height: "210px",
      borderTop: `1px solid ${theme.colors.border}`,
      padding: theme.spacing(4),
    },
    container: {
      width: "100%",
      height: "fit-content",
      position: "relative",
      backgroundColor: theme.colors.white,
    },
    expandButton: {
      position: "absolute",
      width: "fit-content",
      top: 0,
      left: 0,
      right: 0,
      margin: "0 auto",
      border: `1px solid ${theme.colors.border}`,
      transform: "translateY(-50%)",
      borderRadius: `${theme.radius.lg}px`,
      padding: theme.spacing(0.5, 2.5),
      backgroundColor: theme.colors.white,
    },
    aiButton: {
      position: "absolute",
      width: "fit-content",
      top: 0,
      right: theme.spacing(3),
      transform: "translateY(-50%)",
    },
  });

const InputSection = ({
  guestId,
  lastMessage = {},
  skeleton = {enabled: false},
  guestPanelClosed = false,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const {replyEmailProps, setReplyEmailProps} = useContext(MessagesContext);
  const gotBPLink = useRef(false);

  const [allChannelsDisabled, setAllChannelsDisabled] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [bpLink, setBpLink] = useState(null);
  const [inputText, setInputText] = useState(getPredictedResponse(lastMessage));
  const [attachments, setAttachments] = useState([]);
  const [fullEmailInput, setFullEmailInput] = useState(true);
  const [activeChannel, setActiveChannel] = useState(null);
  const [allListingsDisabled, setAllListingsDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [templatesData, setTemplatesData] = useState({
    open: false,
    loading: false,
    templates: [],
    buttonClicked: false,
  });
  const [whatsappData, setWhatsappData] = useState({
    accountId: null,
    templates: [],
    openTemplates: false,
    freeText: false,
    selected: null,
    preview: {},
  });
  const [emailContent, setEmailContent] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    html: "",
    toInput: "",
    ccInput: "",
    bccInput: "",
  });

  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const listings = useSelector((state) => state.defaultReducer.house_data_dict);
  const guestBookings = useSelector(
    (state) => state.defaultReducer.selected_guest_bookings,
  );
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const emailSignature = user_profile.email_signature;
  const readOnlyPermissions =
    !!user_profile.scopes && user_profile.scopes.messaging !== "write";
  const messagesDisabled = allListingsDisabled || readOnlyPermissions;
  const isWhatsappSelected = activeChannel === "whatsapp";
  const isEmailSelected = activeChannel === "email";
  const showEnsoAI =
    (!isWhatsappSelected || whatsappData.freeText) &&
    !messagesDisabled &&
    activeChannel !== "note" &&
    !allChannelsDisabled;
  const flag = useFlags();
  const showAutopilotToggleFlag =
    flag.enableAutopilot &&
    user_profile?.connected_services?.includes("autoresponder");
  console.log("user_profile", user_profile.connected_services);
  const guest = useMemo(() => guests[guestId], [guests, guestId]);

  const guestLoaded = useMemo(
    () => guests[guestId]?.guest_id === guestId && !!guests[guestId].metadata,
    [guests, guestId],
  );
  const guestBookingsLoaded = useMemo(
    () => checkGuestBookingsLoaded(guest, guestId, guestBookings),
    [guestBookings, guestId],
  );
  const saveDisabled = useMemo(() => {
    if (isEmailSelected) {
      return (
        !emailContent.subject ||
        !emailContent.html ||
        emailContent.html === "<p><br></p>" // Empty email
      );
    }
    if (isWhatsappSelected) {
      return !whatsappData.freeText ? !whatsappData.selected : !inputText;
    }
    return !inputText && !attachments.length;
  }, [
    isEmailSelected,
    isWhatsappSelected,
    emailContent,
    whatsappData,
    inputText,
    attachments,
  ]);
  const loadingInput = skeleton.enabled || !guestLoaded || !guestBookingsLoaded;

  // Update WhatsApp input type based on WhatsApp expiry
  useEffect(() => {
    let now = Date.now();
    let WAExpiry = guest?.metadata?.whatsapp_expiry ?? now;
    if (WAExpiry.toString().length === 10) {
      WAExpiry = WAExpiry * 1000;
    }

    const convoInitiated = WAExpiry > now;
    if (convoInitiated !== whatsappData.freeText) {
      setWhatsappData((prev) => ({...prev, freeText: convoInitiated}));
    }
  }, [guest]);

  // Get Boarding Pass link
  useEffect(() => {
    if (!!bpLink || !guest) return;
    if (gotBPLink.current) return;
    gotBPLink.current = true;

    dispatch(
      getGuestKeycardLink(
        guest.guest_id,
        guest.bookings[guest.bookings.length - 1],
        (link) => {
          setBpLink(link);
        },
      ),
    );
  }, [guest, bpLink]);

  // Reset data when switching conversations
  useEffect(() => {
    let isMounted = true;
    gotBPLink.current = false;
    setFullEmailInput(true);
    setInputText(getPredictedResponse(lastMessage));
    setBpLink(null);
    setWhatsappData((prev) => ({
      ...prev,
      openTemplates: false,
      selected: null,
    }));
    setEmailContent({
      to: [],
      cc: [],
      bcc: [],
      subject: "",
      html: `${!!emailSignature ? `<p><br></p>${emailSignature}` : ""}`,
      toInput: "",
      ccInput: "",
      bccInput: "",
    });
    setTemplatesData({
      loading: true,
      search: "",
      templates: [],
      open: false,
      buttonClicked: false,
    });
    dispatch(
      getGuestChatOptions({
        guestId: guestId,
        onSuccess: (response) => {
          if (isMounted) {
            setTemplatesData((prev) => ({
              ...prev,
              loading: false,
              templates: response,
            }));
          }
        },
        onError: () => {
          if (isMounted) {
            setTemplatesData((prev) => ({...prev, loading: false}));
          }
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  }, [guestId]);

  // Update "To" field based on guest email
  useEffect(() => {
    if (!guests[guestId] || !guests[guestId].email || !!emailContent.to[0])
      return;
    setEmailContent((prev) => ({
      ...prev,
      to: !!guests[guestId].email ? [guests[guestId].email] : [],
    }));
  }, [emailContent, guestId, guests]);

  // Check if all listings are disabled
  useEffect(() => {
    if (loadingInput) return;
    const listingKeys = Object.keys(listings ?? {});
    let allDisabled = false;

    if (listingKeys.length && guestBookings?.length) {
      allDisabled = guestBookings.every((booking) => {
        const listing = listings[booking.listing_id];
        return listing?.hasOwnProperty("active") && !listing.active;
      });
    }

    if (allDisabled !== allListingsDisabled) {
      setAllListingsDisabled(allDisabled);
    }
  }, [loadingInput, guestBookings, listings]);

  // Adds predicted response to input text
  useEffect(() => {
    setInputText(getPredictedResponse(lastMessage));
  }, [lastMessage]);

  // Updates email content to reply to email
  useEffect(() => {
    if (replyEmailProps.enabled) {
      setEmailContent((prev) => ({
        to: guests[guestId]?.email ? [guests[guestId].email] : [],
        cc: [],
        bcc: [],
        subject: `Re: ${replyEmailProps.subject}`,
        html: `${!!emailSignature ? `<p><br></p>${emailSignature}` : ""}`,
        toInput: "",
        ccInput: "",
        bccInput: "",
      }));
      handleSelectChannel("email");
      setReplyEmailProps({
        enabled: false,
        subject: "",
      });
    }
  }, [replyEmailProps]);

  const handleSendMessage = () => {
    // Update guest flags
    dispatch(
      editGuest({
        ...guest,
        unread_messages: false,
        unanswered_messages: false,
      }),
    );

    // Prepare message
    let message = inputText;
    if (isWhatsappSelected && whatsappData.selected?.integration_template_id) {
      message =
        whatsappData.preview[whatsappData.selected.integration_template_id];
    } else if (activeChannel === "email") {
      message = {
        ...emailContent,
        html: getEmailContentWrapper(emailContent.html),
      };
    }

    const sent = Date.now();
    const data = {
      body: {
        enso_user_id: user_profile.enso_user_id,
        guest_id: guestId,
        channel: activeChannel,
        message: message,
        enso_key: user_profile.enso_key,
        integration_template_id: whatsappData.selected?.integration_template_id,
        service_id: whatsappData.accountId,
        attachments: !imageChannels.includes(activeChannel)
          ? []
          : attachments.map((att) => att.url),
        sent,
      },
    };

    // Optimistic loading: Add the new message to the conversation
    const newLocalMessage = {...data.body, id: sent, sender_type: "user"};
    dispatch(appendMessage(guestId, newLocalMessage));

    // Send message
    dispatch(
      sendMessages(
        data,
        null,
        (response) => {
          dispatch(
            updateGuest({
              guest_id: guestId,
              multipleFields: {
                unread_messages: false,
                unanswered_messages: false,
              },
              disableAlert: true,
            }),
          );
          dispatch(editMessages(response, sent));
          setInputText("");
          setWhatsappData((prev) => ({
            ...prev,
            openTemplates: false,
            selected: null,
          }));
          setEmailContent({
            to: !!guests[guestId].email ? [guests[guestId].email] : [],
            cc: [],
            bcc: [],
            subject: "",
            html: `${!!emailSignature ? `<p><br></p>${emailSignature}` : ""}`,
            toInput: "",
            ccInput: "",
            bccInput: "",
          });
        },
        () => {
          dispatch(
            openGeneralError({
              open: true,
              message: "Failed to send message",
              disableSeverity: true,
              subtitle:
                "There was a problem trying to send your message, please try again later",
              onClose: () => dispatch(closeGeneralError()),
            }),
          );
          dispatch(
            editMessages({...newLocalMessage, sending_error: true}, sent),
          );
        },
      ),
    );

    // Reset input content
    resetInputContent();
  };

  const resetInputContent = () => {
    setInputText("");
    setWhatsappData((prev) => ({
      ...prev,
      openTemplates: false,
      selected: null,
    }));
    setEmailContent((prev) => ({
      to: prev.to.slice(1),
      cc: [],
      bcc: [],
      subject: "",
      html: `${!!emailSignature ? `<p><br></p>${emailSignature}` : ""}`,
      toInput: "",
      ccInput: "",
      bccInput: "",
    }));
    setAttachments([]);
  };

  const handleSelectChannel = (newChannel) => {
    if (activeChannel !== newChannel && newChannel === "email") {
      setFullEmailInput(true);
    }
    setActiveChannel(newChannel);
  };

  return loadingInput ? (
    <Container className={classes.inputSkeleton}>
      <Skeleton
        width={"72px"}
        height={"20px"}
        style={{borderRadius: `${theme.radius.lg}px`}}
      />
    </Container>
  ) : (
    <FlexContainer
      flexDirection="column"
      className={classes.container}
      gap={theme.spacing(1)}
      sx={{
        padding: activeChannel === "note" ? "0px" : theme.spacing(4, 0),
        borderTop: `1px solid ${activeChannel === "note" ? theme.colors.warning200 : theme.colors.border}`,
      }}
    >
      {/* SCHEDULED MESSAGES MENU - Replace this with the modal design when ready */}
      <ContextMenu
        open={!!anchorEl}
        onClose={() => !keepOpen && setAnchorEl(null)}
        anchorEl={anchorEl}
        modalProps={{
          anchorOrigin: {vertical: "top", horizontal: "end"},
          modalPosition: {vertical: "bottom", horizontal: "end"},
        }}
        customContent={
          <Container p={theme.spacing(1, 3)}>
            <CreateAutoMessageMenu
              guestId={guestId}
              closeMenu={() => setAnchorEl(null)}
              subMenuOpen={keepOpen}
              setSubMenuOpen={setKeepOpen}
              whatsappData={whatsappData}
              availableChannels={availableChannels}
            />
          </Container>
        }
      />

      {/* CHANNELS */}
      <ChannelRow
        guestId={guestId}
        guest={guest}
        lastMessage={lastMessage}
        disabled={messagesDisabled}
        activeChannel={activeChannel}
        setActiveChannel={handleSelectChannel}
        allChannelsDisabled={allChannelsDisabled}
        setAvailableChannels={(channels) => {
          const showInput = channels.some((c) => !!c.show);
          if (showInput === allChannelsDisabled) {
            setAllChannelsDisabled(!showInput);
          }
          setAvailableChannels(channels);
        }}
        whatsappAccountId={whatsappData.accountId}
      />

      {/* EMAIL EXPANDER BUTTON */}
      {isEmailSelected && (
        <ButtonIcon
          icon={
            <SvgIcon
              component={ICONS[fullEmailInput ? "chevron-down" : "chevron-up"]}
              sx={{
                width: "12px",
                height: "12px",
                color: theme.colors.textTertiary,
              }}
            />
          }
          onClick={() => setFullEmailInput((prev) => !prev)}
          className={classes.expandButton}
        />
      )}

      {/* ENSO AI BUTTON */}
      {showEnsoAI && (
        <Container
          className={classes.aiButton}
          sx={{display: "flex", gap: theme.spacing(1)}}
        >
          <EnsoAIButton
            title="Ask EnsoAI"
            handleGeneratedText={(text) =>
              isEmailSelected
                ? setEmailContent((prev) => ({...prev, html: text}))
                : setInputText(text)
            }
            extra_context={isEmailSelected ? emailContent.html : inputText}
            disablePortal={false}
            type={contentTypeEnum.refine_chat}
            guest={guest}
            listing={listings[guest?.listings?.[0]]}
            latest_message={lastMessage}
          />
          {showAutopilotToggleFlag && <ChatbotToggleButton guest={guest} />}
        </Container>
      )}

      {/* INPUT COMPONENT */}
      {!messagesDisabled &&
        (!allChannelsDisabled || activeChannel === "note") && (
          <ConversationInputComponent
            guestId={guestId}
            channel={activeChannel}
            inputText={inputText}
            bpLink={bpLink}
            templatesData={templatesData}
            setTemplatesData={setTemplatesData}
            setInputText={setInputText}
            componentProps={{
              fullEmailInput,
              emailContent,
              setEmailContent,
              whatsappData,
              setWhatsappData,
            }}
          />
        )}
      {!messagesDisabled &&
        !allChannelsDisabled &&
        activeChannel !== "note" && (
          <ActionsRow
            guestId={guestId}
            bpLink={bpLink}
            sendButtonSpacing={guestPanelClosed}
            disableAttachments={isWhatsappSelected && !whatsappData.freeText}
            disableImages={!imageChannels.includes(activeChannel)}
            saveDisabled={saveDisabled}
            onTemplatesOpen={() => {
              if (isWhatsappSelected) {
                setWhatsappData((prev) => ({...prev, openTemplates: true}));
              } else {
                setTemplatesData((prev) => ({
                  ...prev,
                  open: true,
                  buttonClicked: true,
                }));
              }
            }}
            onScheduleOpen={(e) => setAnchorEl(e.currentTarget)}
            attachedImages={
              !imageChannels.includes(activeChannel) ? [] : attachments
            }
            setAttachedImages={setAttachments}
            insertText={(text) =>
              isEmailSelected
                ? setEmailContent((prev) => ({
                    ...prev,
                    html: `${prev.html} ${text}`,
                  }))
                : setInputText(`${inputText} ${text}`)
            }
            sendMessage={handleSendMessage}
          />
        )}

      {/* DISABLED INPUT */}
      {messagesDisabled && (
        <DisabledInput readOnlyPermissions={readOnlyPermissions} />
      )}
    </FlexContainer>
  );
};

export default InputSection;
